import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { Alert, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { ory } from 'src/auth/ory';
import { Logger } from 'src/utils/Logger';
import { usePublicTenantConfig } from 'src/auth/hooks/usePublicTenantConfig';
import { useInviteSettingsFlow } from 'src/auth/hooks/useInviteSettingsFlow';
import { useAuthMethodCheck } from 'src/auth/hooks/useAuthMethodCheck';
import { ExpiredInviteSection } from 'src/auth/invite/ExpiredInviteSection';
import { InviteRegistrationForm } from 'src/auth/invite/InviteRegistrationForm';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { SuccessMessage } from 'src/auth/components/SuccessMessage';
import { setInviteData } from 'src/auth/utils/inviteStorage';
import { filterOidcNodesByAuthMethod, isSsoOnly } from 'src/auth/utils/ory-helpers';
import { AuthMethod } from 'src/auth/settings/types';
import { OidcSection } from 'src/components/convenience/auth/OidcSection';
import { AuthCard } from 'src/auth/components/AuthCard';
import { TermsAndPrivacy } from 'src/auth/components/TermsAndPrivacy';
import { AuthFeedback } from 'src/auth/components/AuthFeedback';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const InvitePageRoute = () => {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow');
  const isExpired = searchParams.get('expired') === 'true';

  if (isExpired) {
    return <ExpiredInvitePage />;
  }

  if (flowId) {
    return <InvitePageWithFlowId flowId={flowId} />;
  }

  return <InvitePageWithoutFlow />;
};

const ExpiredInvitePage = () => {
  const handleLoginClick = () => {
    window.location.replace('/login');
  };

  return (
    <AuthPageLayout withHeader>
      <ExpiredInviteSection onLoginClick={handleLoginClick} />
    </AuthPageLayout>
  );
};

const InvitePageWithFlowId = ({ flowId }: { flowId: string }) => {
  const authState = useAuthMethodCheck(flowId);

  if (authState === 'loading') {
    return <AuthPageLayout isLoading />;
  }

  if (authState === 'existing_user') {
    return <Navigate to="/login" replace />;
  }

  return <InvitePage flowId={flowId} />;
};

const InvitePageWithoutFlow = () => {
  const creatingFlowRef = useRef(false);

  useEffect(() => {
    if (!creatingFlowRef.current) {
      creatingFlowRef.current = true;
      ory
        .createBrowserSettingsFlow()
        .then(({ data }) => {
          window.location.replace(`/invite?flow=${data.id}`);
        })
        .catch(error => {
          Logger.error('Failed to create settings flow for invite page', error);
          window.location.replace('/login');
        });
    }
  }, []);

  return <AuthPageLayout isLoading />;
};

const InvitePage = ({ flowId }: { flowId: string }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    isLoading: isFlowLoading,
    error,
    userEmail,
    tenantName,
    oidcNodes,
    csrfToken,
    formAction,
    updatePassword,
  } = useInviteSettingsFlow(flowId);

  const {
    config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = usePublicTenantConfig(tenantName || undefined);

  const isLoading = isFlowLoading || isConfigLoading;
  const variant: AuthMethod = !isConfigError && config?.authMethod ? config.authMethod : 'password';

  const filteredOidcNodes = useMemo(
    () => filterOidcNodesByAuthMethod(oidcNodes, variant),
    [oidcNodes, variant],
  );

  useEffect(() => {
    if (tenantName && userEmail) {
      fetch(
        `${API_URL}/public/invite-used/${encodeURIComponent(tenantName)}/${encodeURIComponent(userEmail)}`,
        { method: 'POST' },
      ).catch(e => Logger.error('Failed to mark invite as used', e));
    }
  }, [tenantName, userEmail]);

  const handleCreateAccount = async (password: string) => {
    setIsSubmitting(true);
    const { success } = await updatePassword(password);
    setIsSubmitting(false);

    if (success) {
      setInviteData(userEmail, tenantName);
      setShowSuccess(true);
    }
  };

  const handleSuccessRedirect = async () => {
    try {
      const { data: logoutFlow } = await ory.createBrowserLogoutFlow();
      await ory.updateLogoutFlow({ token: logoutFlow.logout_token });
    } catch (logoutError) {
      Logger.error(
        'Logout failed after credential setup, redirecting to login anyway',
        logoutError,
      );
    }
    window.location.replace('/login');
  };

  const ssoOnly = isSsoOnly(variant);

  if (isConfigError) {
    return (
      <AuthPageLayout isLoading={isLoading} withHeader>
        <AuthCard>
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Something went wrong. Please try refreshing the page.
          </Alert>
        </AuthCard>
      </AuthPageLayout>
    );
  }

  if (showSuccess) {
    return (
      <AuthPageLayout isLoading={isLoading} withHeader>
        <SuccessMessage variant="invite" onAction={handleSuccessRedirect} />
      </AuthPageLayout>
    );
  }

  if (ssoOnly) {
    return (
      <AuthPageLayout isLoading={isLoading} withHeader>
        <AuthCard>
          <Stack gap="md">
            <Text fz="xl" fw={FONT_WEIGHTS.semibold} ta="center">
              Link your organization account
              <br />
              to complete setup
            </Text>

            <AuthFeedback error={error} />

            {filteredOidcNodes.length > 0 ? (
              <OidcSection
                nodes={filteredOidcNodes}
                formAction={formAction}
                csrfToken={csrfToken}
                action="link"
              />
            ) : (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
                No SSO providers configured. Please contact your administrator.
              </Alert>
            )}

            <TermsAndPrivacy />
          </Stack>
        </AuthCard>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout isLoading={isLoading} withHeader>
      <InviteRegistrationForm
        userEmail={userEmail}
        error={error}
        isSubmitting={isSubmitting}
        onSubmit={handleCreateAccount}
        oidcNodes={oidcNodes}
        formAction={formAction}
        csrfToken={csrfToken}
      />
    </AuthPageLayout>
  );
};
